<template>
    <PanelFrame ref="panelFrame" panel-class="music-app" resize-directions="all" :content-class="panelContentClass">
        <template #titlebar-left>
            <button v-if="isCompact && mobileView === 'main'" class="panel-titlebar-btn" @click.stop="mobileView = 'sidebar'">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <button v-else class="panel-titlebar-btn" @click.stop="sidebarOpen = !sidebarOpen" :title="$t('common.music.sidebar')">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="2" y="2" width="20" height="20" rx="2" ry="2"/><line x1="9" y1="2" x2="9" y2="22"/>
                </svg>
            </button>
            <span class="music-titlebar-title">{{ titlebarText }}</span>
        </template>

        <template #titlebar-actions>
            <button class="panel-titlebar-btn" @click.stop="onRefresh" :title="$t('common.music.refresh')">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
            </button>
            <button v-if="ms.state.playing || ms.state.buffering" class="panel-titlebar-btn" @click.stop="ms.stop()" :title="$t('common.music.stop')">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="4" y="4" width="16" height="16" rx="2"/></svg>
            </button>
        </template>

        <template #default>
            <div class="music-body">
                <MusicSidebar v-if="showSidebar"
                    :width="isCompact ? null : sidebarWidth"
                    @navigate="onNavigate"
                    @divider-start="startSidebarResize"/>
                <div v-show="showMain" class="music-main-wrap">
                    <MusicContent :class="{'music-main--blurred': showNowPlaying && ms.currentSource.value}"
                        @play-station="onPlayStation"
                        @upload="onUpload"/>
                    <MusicNowPlaying v-if="showNowPlaying && ms.currentSource.value"
                        @collapse="showNowPlaying = false"/>
                    <MusicPlayerBar @toggle-now-playing="onToggleNowPlaying"/>
                </div>
            </div>
        </template>
    </PanelFrame>
</template>

<script>
import './music-panel.css'
import { createPanelMixin } from '@/components/panel/usePanelBase'
import PanelFrame from '@/components/panel/PanelFrame.vue'
import MusicSidebar from './MusicSidebar.vue'
import MusicContent from './MusicContent.vue'
import MusicPlayerBar from './MusicPlayerBar.vue'
import MusicNowPlaying from './MusicNowPlaying.vue'
import musicService from './musicService'
import fileManagerPicker from '@/components/filemanager/fileManagerPicker'

const musicPanelMixin = createPanelMixin({
    panelId: 'musicPanel',
    shortcut: { code: 'KeyM' },
    minWidth: 320,
    minHeight: 400,
    defaults: {
        floatingMode: false,
        width: 1000,
        height: 650,
        sidebarWidth: 250,
    },
    hasInnerLayout: true,
    showSettingsInCompact: true,
    minSidebar: 180,
    maxSidebar: 320,
    settingsItems: [
        'alwaysOnTop',
        'openOnLoad',
        'divider',
        'transparent',
        'divider',
        { key: 'animationsEnabled', label: 'common.music.animations', icon: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>', default: true },
        'divider',
        'resetWindowSize',
        'resetInnerLayout',
    ],
    onOpen(vm) {
        vm.$nextTick(() => musicService.onPanelOpen())
    },
    onContext(vm, ctx) {
        vm.$nextTick(async () => {
            if (ctx.playlistId) {
                musicService.setActiveTab('library')
                musicService.setActivePlaylist(ctx.playlistId)
                vm.showNowPlaying = false
            } else if (ctx.trackId) {
                musicService.setActiveTab('library')
                if (musicService.state.activePlaylistId) {
                    musicService.setActivePlaylist(null)
                }
                if (!musicService.state._libraryFetched) {
                    await musicService.loadTracks()
                }
                const track = musicService.state.libraryTracks.find(t => t.id === ctx.trackId)
                if (track) musicService.playTrack(track)
            }
        })
    },
    extraStorageKeys(vm) {
        return { sidebarOpen: vm.sidebarOpen, showNowPlaying: vm.showNowPlaying }
    },
})

export default {
    name: 'MusicPanel',
    mixins: [musicPanelMixin],
    components: { PanelFrame, MusicSidebar, MusicContent, MusicPlayerBar, MusicNowPlaying },
    data() {
        const saved = this.panelLoadLayout()
        return {
            sidebarOpen: saved.sidebarOpen ?? true,
            showNowPlaying: saved.showNowPlaying ?? false,
            mobileView: 'sidebar',
        }
    },
    computed: {
        ms() {
            return musicService
        },
        panelContentClass() {
            return 'music-active'
        },
        titlebarText() {
            if (!this.sidebarOpen && musicService.state.playing) {
                return musicService.displayName.value || this.$t('common.music.title')
            }
            return this.$t('common.music.title')
        },
        showSidebar() {
            return this.isCompact ? this.mobileView === 'sidebar' : this.sidebarOpen
        },
        showMain() {
            return !this.isCompact || this.mobileView === 'main'
        },
    },
    methods: {
        onResetInnerLayout() {
            this.sidebarWidth = 250
            this.sidebarOpen = true
            this.panelSaveLayoutDebounced()
        },
        onPlayStation(station) {
            musicService.playStation(station)
            if (this.isCompact) {
                this.mobileView = 'main'
            }
        },
        onNavigate() {
            this.showNowPlaying = false
            if (this.isCompact) {
                this.mobileView = 'main'
            }
        },
        onToggleNowPlaying() {
            if (!musicService.currentSource.value) return
            this.showNowPlaying = !this.showNowPlaying
        },
        onRefresh() {
            if (musicService.state.activeTab === 'radio') {
                const cat = musicService.state.radioCategory
                if (cat === 'popular') musicService.fetchPopularStations()
                else if (cat === 'genres' && musicService.state.radioGenre) {
                    musicService.fetchStationsByTag(musicService.GENRE_TAGS[musicService.state.radioGenre] || musicService.state.radioGenre)
                }
            } else {
                musicService.loadPlaylists()
                musicService.loadTracks()
            }
        },
        onUpload() {
            const playlistId = musicService.state.activePlaylistId || musicService.libraryPlaylist.value?.id
            if (!playlistId) return
            fileManagerPicker.open({
                uploadableType: 'MODULE_MUSIC_PLAYLIST',
                uploadableId: playlistId,
                accept: 'audio/*',
                multiple: true,
                canDelete: false,
                canDetach: true,
                onUpload: () => {
                    musicService.loadPlaylists()
                    musicService.loadTracks()
                },
                onDetach: () => {
                    musicService.loadPlaylists()
                    musicService.loadTracks()
                },
                onClose: () => {
                    musicService.loadPlaylists()
                    musicService.loadTracks()
                },
            })
        },
    },
    watch: {
        sidebarOpen() { this.panelSaveLayoutDebounced() },
        showNowPlaying() { this.panelSaveLayoutDebounced() },
    },
    mounted() {
        this.$nextTick(() => {
            this.$watch('panelWidth', (w, old) => {
                if (!this.isCompact && w < 520 && old >= 520 && this.sidebarOpen) {
                    this.sidebarOpen = false
                }
            })
        })
    },
}
</script>
